<template>
  <div data-focus-view>
    <nav class="tw:!flex tw:!items-center tw:!text-sm tw:!text-gray-500 tw:!mb-6 tw:!flex-wrap tw:!gap-2">
      <a
        href="#"
        @click.prevent="emit('back-home')"
        class="tw:!text-gray-500 tw:!no-underline tw:!cursor-pointer tw:!transition-colors od-nav-hover"
      >
        {{ $t('nav.esimShop') }}
      </a>
      <ChevronRight class="tw:!w-4 tw:!h-4 tw:!text-gray-400" />
      <a
        href="#"
        @click.prevent="emit('back')"
        class="tw:!no-underline tw:!cursor-pointer tw:!transition-colors od-nav-hover tw:!text-gray-500"
      >
        {{ store.countryName }}
      </a>
      <ChevronRight class="tw:!w-4 tw:!h-4 tw:!text-gray-400" />
      <span class="tw:!font-semibold od-nav-active">{{ $t('register.breadcrumb') }}</span>
    </nav>

    <div class="register-layout">
      <div class="tw:!bg-white tw:!rounded-3xl tw:!p-6 tw:!shadow-sm tw:!order-2 md:tw:!order-1">
        <h3 class="tw:!text-xl tw:!font-bold tw:!text-gray-900 tw:!m-0 tw:!mb-6">{{ $t('checkout.section.package') }}</h3>

        <div class="tw:!flex tw:!items-center tw:!gap-4 tw:!pb-6 tw:!border-b tw:!border-gray-200 tw:!mb-6">
          <div class="tw:!w-14 tw:!h-14 tw:!rounded-full tw:!overflow-hidden tw:!border-2 tw:!border-gray-50 tw:!shrink-0">
            <img
              :src="`https://flagcdn.com/${store.countrySlug.toLowerCase()}.svg`"
              :alt="store.countryName"
              class="tw:!w-full tw:!h-full tw:!object-cover"
            />
          </div>
          <div class="tw:!flex tw:!flex-col tw:!gap-1">
            <h2 class="tw:!text-xl tw:!font-bold tw:!text-gray-900 tw:!m-0">{{ store.countryName }}</h2>
            <div class="tw:!flex tw:!gap-4 tw:!mt-1">
              <div class="tw:!flex tw:!items-center tw:!gap-2 tw:!text-sm tw:!text-gray-500">
                <Wifi class="tw:!h-4 tw:!w-4" /> {{ store.selectedPackage?.speed }}
              </div>
              <div class="tw:!flex tw:!items-center tw:!gap-2 tw:!text-sm tw:!text-gray-500">
                <ClockArrowUp class="tw:!h-4 tw:!w-4" /> {{ store.selectedPackage?.unusedValidTime }} {{ $t('checkout.field.days') }}
              </div>
            </div>
          </div>
        </div>

        <div class="tw:!flex tw:!flex-col tw:!gap-3 tw:!mb-6">
          <div class="tw:!flex tw:!items-center tw:!gap-3">
            <MapPin class="tw:!w-5 tw:!h-5 tw:!text-gray-400 tw:!shrink-0" />
            <span class="tw:!text-sm tw:!text-gray-500">{{ $t('checkout.field.coverage') }}:</span>
            <span class="tw:!text-sm tw:!font-semibold tw:!text-gray-900">{{ store.countryName }}</span>
          </div>
          <div class="tw:!flex tw:!items-center tw:!gap-3">
            <ArrowDownUp class="tw:!w-5 tw:!h-5 tw:!text-gray-400 tw:!shrink-0" />
            <span class="tw:!text-sm tw:!text-gray-500">{{ $t('checkout.field.traffic') }}:</span>
            <span class="tw:!text-sm tw:!font-semibold tw:!text-gray-900">{{ formatData(store.selectedPackage?.volume || 0) }}</span>
          </div>
          <div class="tw:!flex tw:!items-center tw:!gap-3">
            <CalendarCheck class="tw:!w-5 tw:!h-5 tw:!text-gray-400 tw:!shrink-0" />
            <span class="tw:!text-sm tw:!text-gray-500">{{ $t('checkout.field.validity') }}:</span>
            <span class="tw:!text-sm tw:!font-semibold tw:!text-gray-900">{{ store.selectedPackage?.description }}</span>
          </div>
        </div>

        <div class="tw:!flex tw:!justify-between tw:!items-center tw:!pt-4 tw:!border-t tw:!border-gray-200 tw:!mb-4">
          <span class="tw:!text-lg tw:!font-bold tw:!text-gray-900">{{ $t('checkout.field.total') }}:</span>
          <span class="tw:!text-2xl tw:!font-bold tw:!text-gray-900">${{ formatPrice(store.totalAmount) }}</span>
        </div>

        <div class="tw:!flex tw:!flex-col tw:!gap-2">
          <div class="tw:!flex tw:!items-center tw:!gap-3 tw:!bg-green-50 tw:!rounded-xl tw:!px-4 tw:!py-3">
            <Zap class="tw:!w-4 tw:!h-4 tw:!text-green-500 tw:!shrink-0" />
            <span class="tw:!text-sm tw:!text-green-800">{{ $t('checkout.trust.instantDelivery') }}</span>
          </div>
          <div class="tw:!flex tw:!items-center tw:!gap-3 tw:!bg-green-50 tw:!rounded-xl tw:!px-4 tw:!py-3">
            <RefreshCw class="tw:!w-4 tw:!h-4 tw:!text-green-500 tw:!shrink-0" />
            <span class="tw:!text-sm tw:!text-green-800">{{ $t('checkout.trust.moneyBack') }}</span>
          </div>
          <div class="tw:!flex tw:!items-center tw:!gap-3 tw:!bg-green-50 tw:!rounded-xl tw:!px-4 tw:!py-3">
            <MailPlus class="tw:!w-4 tw:!h-4 tw:!text-green-500 tw:!shrink-0" />
            <span class="tw:!text-sm tw:!text-green-800">{{ $t('checkout.trust.support') }}</span>
          </div>
        </div>

        <img :src="visaTag" alt="Payment" class="tw:!w-xs tw:!mt-4 tw:!object-contain tw:!mx-auto" />
      </div>

      <div class="tw:!bg-white tw:!rounded-3xl tw:!p-6 tw:!shadow-sm tw:!order-1 md:tw:!order-2">
        <h3 class="tw:!text-xl tw:!font-bold tw:!text-gray-900 tw:!m-0 tw:!mb-2">{{ $t('register.title') }}</h3>

        <div class="tw:!mb-6">
          <label for="email" class="tw:!block tw:!text-sm tw:!font-semibold tw:!text-gray-700 tw:!mb-2">
            Email <span class="tw:!text-red-500">*</span>
          </label>
          <div class="tw:!relative">
            <Mail class="tw:!absolute tw:!left-3 tw:!top-1/2 tw:!-translate-y-1/2 tw:!w-5 tw:!h-5 tw:!text-gray-400 tw:!pointer-events-none" />
            <input
              id="email"
              v-model="email"
              type="email"
              placeholder="your@gmail.com"
              @blur="handleBlur"
              @keyup.enter="checkout(store.selectedPackage!, store.selectedCount, email)"
              :class="[
                'tw:!w-full tw:!pl-10 tw:!pr-4 tw:!py-3 tw:!rounded-xl tw:!text-sm tw:!text-gray-900 tw:!bg-white tw:!box-border od-input',
                emailMeta.touched && !emailMeta.valid
                  ? 'tw:!border-red-400 focus:tw:!border-red-500 focus:tw:![box-shadow:none]'
                  : ''
              ]"
            />
          </div>
          <p v-if="emailMeta.touched && errorMessage" class="tw:!text-xs tw:!text-red-500 tw:!mt-1 tw:!m-0">
            {{ errorMessage }}
          </p>
        </div>

        <button
          type="button"
          @click="checkout(store.selectedPackage!, store.selectedCount, email)"
          class="tw:!w-full tw:!py-3 tw:!px-6 tw:!font-semibold tw:!rounded-xl tw:!border-0 tw:!text-sm tw:!block od-cta-btn"
        >
          {{ store.loading.order ? $t('button.loading') : $t('button.continue') }}
        </button>

        <div class="tw:!flex tw:!items-center tw:!justify-center tw:!gap-2 tw:!mt-4">
          <ShieldCheck class="tw:!w-4 tw:!h-4 tw:!text-gray-500" />
          <span class="tw:!text-xs tw:!text-gray-500">{{ $t('checkout.payment.secureEncrypted') }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import visaTag from '~/assets/images/visa-tag.webp'
import { useField } from 'vee-validate'
import { ChevronRight, Mail, MapPin, CalendarCheck, ArrowDownUp, Wifi, ClockArrowUp, RefreshCw, ShieldCheck, Zap, MailPlus } from 'lucide-vue-next'
import { useStore } from '~/composables/widgetMode'
import type { PackageList } from '~/composables/types/esimPackage'
import { formatData, formatPrice } from '~/utility/helper'
import * as yup from 'yup'

const store = useStore()

const emit = defineEmits<{
  'back': []
  'back-home': []
  'checkout': [pkg: PackageList, count: number, email: string]
}>()

const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/

const schema = yup
  .string()
  .required('Email address is required.')
  .trim()
  .lowercase()
  .matches(EMAIL_REGEX, 'Please enter a valid email address.')

const {
  value: email,
  errorMessage,
  meta: emailMeta,
  handleBlur,
  setTouched,
  validate: triggerEmailValidation
} = useField<string>('email', schema, {
  validateOnValueUpdate: true,
  initialValue: store.registerEmail || ''
})

const checkout = async (pkg: PackageList, count: number, email: string) => {
  if (!pkg) {
    emit('back')
    return
  }

  if (store.loading.order) return

  setTouched(true)

  const ok = await triggerEmailValidation()
  if (!ok.valid) return

  const emailValue = (email || '').trim().toLowerCase()
  store.registerEmail = emailValue
  emit('checkout', pkg, count, emailValue)
}
</script>

<style scoped>
.register-layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  align-items: start;
}

@media (max-width: 768px) {
  .register-layout {
    grid-template-columns: 1fr;
  }
}

.continue-btn {
  background: var(--esim-primary);
  border: 2px solid rgba(255, 255, 255, 0.3);
  box-shadow:
    0 2px 12px var(--esim-primary-glow),
    0 0 0 1px var(--esim-primary-glow);
  transition: all 0.2s ease-in-out;
}

.continue-btn:hover {
  background: var(--esim-primary-hover);
  box-shadow:
    0 4px 20px var(--esim-primary-glow-hover),
    0 0 0 2px var(--esim-primary-glow);
  transform: translateY(-1px);
}

.od-nav-active {
  color: var(--esim-primary, #facc15);
}
</style>